import axiosInstance from '../../api/axios';

/**
 * Client helpers for the (sample x attribute) evidence mapping matrix.
 * Backend persists the reviewed matrix; AI map-evidence is pure compute.
 */

export function attrCellId(attr) {
  return attr?.id || attr?.key || attr?.ref;
}

export function cellKey(sampleRef, attr) {
  return `${sampleRef}||${attrCellId(attr)}`;
}

/** Normalize API / AI cells into a keyed map aligned with current attributes. */
export function indexEvidenceCells(list, attributes = []) {
  const byRef = {};
  const byId = {};
  attributes.forEach((a) => {
    const id = attrCellId(a);
    byRef[a.ref] = id;
    byId[id] = id;
    if (a.label) byId[a.label] = id;
  });

  const m = {};
  (list || []).forEach((c) => {
    const sample = c.sample_ref || c.record_ref;
    const rawAttr = c.attribute_id || c.attributeId;
    const attrRef = c.attribute_ref || '';
    const attrId = byId[rawAttr] || byRef[attrRef] || byRef[rawAttr] || rawAttr || attrRef;
    if (!sample || !attrId) return;
    const review = c.review_status || c.status;
    m[`${sample}||${attrId}`] = {
      sample_ref: sample,
      attribute_id: attrId,
      attribute_ref: attrRef || (attributes.find((a) => attrCellId(a) === attrId)?.ref ?? ''),
      file_id: c.file_id ?? c.evidence_id ?? null,
      file_name: c.file_name || '',
      locator: c.locator || null,
      excerpt: c.excerpt || '',
      confidence: c.confidence ?? 0,
      source: c.source || 'auto',
      status: review || (Number(c.confidence) >= 90 ? 'confirmed' : 'pending'),
    };
  });
  return m;
}

export function cellsToPayload(cells) {
  return Object.values(cells || {})
    .filter((c) => c.sample_ref && c.attribute_id)
    .map((c) => ({
      record_ref: c.sample_ref,
      sample_ref: c.sample_ref,
      attribute_id: c.attribute_id,
      attribute_ref: c.attribute_ref || '',
      evidence_id: c.file_id ?? c.evidence_id ?? null,
      file_id: c.file_id ?? c.evidence_id ?? null,
      file_name: c.file_name || '',
      locator: c.locator || null,
      excerpt: c.excerpt || '',
      confidence: c.confidence ?? 0,
      source: c.source || 'missing',
      review_status: c.status === 'confirmed' ? 'confirmed' : (c.review_status || 'pending'),
    }));
}

export function mergeAiEvidenceCells(prev, aiList, mode, attributes) {
  const ai = indexEvidenceCells(aiList, attributes);
  if (mode === 'all') return ai;
  const merged = { ...prev };
  Object.entries(ai).forEach(([k, v]) => {
    const existing = prev[k];
    if (existing?.file_name) return; // gaps: keep manual and existing AI mappings
    merged[k] = v;
  });
  return merged;
}

export function countManualMapped(cells) {
  return Object.values(cells || {}).filter((c) => c.source === 'manual' && c.file_name).length;
}

// Create (or fetch the reusable) attribute_test_run that the evidence map and
// the run both hang off. Prefer the selected run id — never swap to "latest".
export async function ensureAttributeRun({ controlUuid, controlId, testingPeriodStart, testingPeriodEnd }) {
  const isoDate = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);
  try {
    const latest = await axiosInstance.get(`/api/control-test-runs/control/${controlUuid}/latest`);
    const run = latest.data?.run;
    const st = run.workflow_status || run.status || '';
    if (run && ['draft', 'period_selected', 'population_configured', 'in_progress', 'pending'].includes(st)) {
      return run.id;
    }
  } catch { /* fall through to create */ }
  const created = await axiosInstance.post('/api/control-test-runs', {
    sox_control_id: controlUuid,
    control_id: controlId,
    testing_period_start: isoDate(testingPeriodStart),
    testing_period_end: isoDate(testingPeriodEnd),
  });
  return created.data.id;
}

export async function aiMapEvidence({ controlUuid, controlTitle, controlObjective, samples, attributes, fields, evidenceFiles }) {
  const resp = await axiosInstance.post('/api/attribute-test-runs/map-evidence', {
    control_id: controlUuid,
    control_title: controlTitle || '',
    control_objective: controlObjective || '',
    samples,
    attributes,
    fields: fields || [],
    // Pass file metadata explicitly so the AI service doesn't need a DB lookup.
    // Each entry: { id: control_upload_id, filename, relative_path }. The folder path lets the AI service tell
    // same-named files apart for the model ("INV1043 - approval.pdf").
    evidence_files: (evidenceFiles || []).map((f) => ({
      id: f.control_upload_id ?? f.id,
      filename: f.filename || f.file_name || f.name || `file_${f.control_upload_id ?? f.id}`,
      relative_path: f.relative_path ?? null,
    })).filter((f) => f.id != null),
  }, { timeout: 1200000 });
  return resp.data?.cells || [];
}

export async function getEvidenceMap(runId) {
  const resp = await axiosInstance.get(`/api/attribute-test-runs/${runId}/evidence-map`);
  return resp.data?.cells || [];
}

export async function saveEvidenceMap(runId, cells) {
  const payload = Array.isArray(cells) ? cells : cellsToPayload(cells);
  await axiosInstance.put(`/api/attribute-test-runs/${runId}/evidence-map`, { cells: payload });
}
